// notifyHomeownerPaymentDue — May 8, 2026 (Greg's "99/100 pay through portal" rule)
//
// Sends the homeowner an SMS with their portal payment link and the live
// amount due. Fires automatically when a deal hits a collectible state (e.g.
// `build_submitted` after install meeting), and from the manual "Send pay
// link" button on the deal detail page.
//
// SMS goes via the Bardin customer line (470) 823-8816 through the existing
// twilio-send-sms edge function (so the conversation stays threaded with the
// homeowner's other texts and any reply lands on Shannon/RM/notification board
// per the existing inbound routing rules).
//
// Email is intentionally NOT sent here — Stripe handles receipt emails on the
// post-payment side (when customer_email is set on the PaymentIntent), and
// Greg's rule is "if no email, text" — meaning text is sufficient. A separate
// helper can layer email on top later if ever wanted.

// The signed-in staff client: this runs from staff pages (TransitionButton,
// AdminControlsPanel), and twilio-send-sms v20 only answers staff and server
// callers. It used the anonymous client until Oct 21, 2026.
import { supabase } from "./supabase";
import { calcScope } from "./scopeCalc";
import { fmtSupabaseError } from "./fmtError";

// Mirror of create-payment-link's POST_BUILD_STAGES + PaymentPortal's same set.
const POST_BUILD_STAGES = new Set([
  "schedule_build", "build_scheduled", "roof_complete",
  "trades_in_progress", "trades_complete",
  "commission_ready", "commission_approved", "commission_paid",
  "register_oc_warranty",
  "ready_for_supps", "ready_for_updated_invoice",
  "supps_waiting_on_claims", "supps_waiting_on_final_invoice",
  "collecting", "delinquent", "collect_payment",
  "closed_won",
]);

interface DealForPayLink {
  id: string;
  // `stage` is widened to `string | null` to match the canonical Deal type
  // (May 9, 2026 fix). Callers were passing a Deal whose stage is nullable,
  // which previously failed strict tsc. The function gates on stage below
  // and skips sending if it's missing — null stage means the deal is in an
  // unknown state and we shouldn't be auto-texting a pay link.
  stage: string | null;
  homeowner_name: string | null;
  homeowner_phone: string | null;
  property_street: string | null;
  // scope-calc inputs (to compute live amount)
  rcv_total?: number | null;
  depreciation_total?: number | null;
  deductible?: number | null;
  supplements?: number | null;
  supplement_invoice_amount?: number | null;
  supplement_approved_amount?: number | null;
  pwi_code?: number | null;
  upgrades?: number | null;
  retail_profit?: number | null;
  at_cost_retail?: number | null;
  acv_credit?: number | null;
  dep_credit?: number | null;
  misc_credit?: number | null;
  acv_only?: boolean | null;
  // Retail option (Jun 11, 2026) — calcScope's retail branch reads these;
  // pre-build amount due becomes the 50% deposit.
  is_retail?: boolean | null;
  retail_contract_total?: number | null;
  // Oct 29, 2026 — trades billed to the homeowner from the trade sheet (SQL
  // trade_sync_deal). calcScope adds trade_ho_total to the invoice (due at
  // completion, never the first payment), so the post-build fallback amount
  // (scope.initialInvoice) needs it; the floor / lines / shortfalls ride along
  // for the same calc.
  trade_ho_total?: number | null;
  trade_ho_floor?: number | null;
  trade_ho_lines?: unknown;
  trade_margin_loss?: number | null;
  pre_build_amount_received?: number | null;
  post_build_amount_received?: number | null;
  pre_build_amount_in_flight?: number | null;
  post_build_amount_in_flight?: number | null;
  total_contract_amount?: number | null;
  initial_invoice_amount?: number | null;
  final_invoice_amount?: number | null;
  updated_invoice_amount?: number | null;
}

function fmtUSD(n: number) {
  return "$" + n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Generate the same base64 portal token format as useHomeownerAuth expects.
 * `lastName` is lower-cased on decode side; `streetNum` is the leading digits
 * of the property street (matching the `^(\d+)` regex in useHomeownerAuth).
 */
function buildPortalToken(deal: DealForPayLink): string | null {
  const fullName = (deal.homeowner_name || "").trim();
  if (!fullName) return null;
  const parts = fullName.split(/\s+/);
  const lastName = parts[parts.length - 1];
  if (!lastName) return null;
  const streetMatch = (deal.property_street || "").match(/^(\d+)/);
  const streetNum = streetMatch?.[1];
  if (!streetNum) return null;
  return btoa(`${deal.id}:${lastName}:${streetNum}`);
}

/** Compute outstanding balance + slot label using the same logic as the
 *  PaymentPortal so the SMS amount matches what the HO will see in the portal. */
function computeOutstanding(deal: DealForPayLink): {
  amount: number;
  slot: "pre_build" | "post_build";
  slotLabel: string;
} {
  const scope = calcScope(deal as unknown as Record<string, unknown>);
  const slot: "pre_build" | "post_build" = deal.stage && POST_BUILD_STAGES.has(deal.stage)
    ? "post_build"
    : "pre_build";
  // Paid = cleared + in-flight (check in the iink pipeline, Stripe at
  // 'made'). Never text a homeowner a balance that includes money they've
  // already handed over. Jun 10, 2026 — matches StatusPortal/PaymentPortal
  // and create-payment-link v6.
  const prePaid = Number(deal.pre_build_amount_received ?? 0)
    + Number(deal.pre_build_amount_in_flight ?? 0);
  const postPaid = Number(deal.post_build_amount_received ?? 0)
    + Number(deal.post_build_amount_in_flight ?? 0);
  const totalPaid = prePaid + postPaid;
  const invoiced = Number(
    deal.final_invoice_amount ??
    deal.updated_invoice_amount ??
    deal.initial_invoice_amount ??
    scope.initialInvoice ??
    0,
  );
  const amount = slot === "pre_build"
    ? Math.max(0, scope.firstPaymentToBardin - prePaid)
    : Math.max(0, invoiced - totalPaid);
  const slotLabel = slot === "pre_build"
    ? (deal.is_retail === true ? "50% deposit" : "first payment")
    : (deal.is_retail === true ? "final payment" : "final invoice");
  return { amount, slot, slotLabel };
}

export interface NotifyResult {
  ok: boolean;
  sent: boolean;
  reason?: string;
}

/**
 * Send the homeowner an SMS with their portal pay link + amount due.
 * Returns `{ ok: true, sent: false, reason }` (without throwing) when there's
 * a known reason to skip (no phone, no balance, etc.) so callers can be
 * fire-and-forget.
 */
export async function notifyHomeownerPaymentDue(
  deal: DealForPayLink,
): Promise<NotifyResult> {
  if (!deal.homeowner_phone) {
    return { ok: true, sent: false, reason: "no homeowner phone on file" };
  }
  // Null stage = deal is in an unknown state. Don't auto-send a pay link
  // because computeOutstanding can't pick the right slot (pre vs post build)
  // without it. Skip silently.
  if (!deal.stage) {
    return { ok: true, sent: false, reason: "no stage on deal" };
  }

  const { amount, slotLabel } = computeOutstanding(deal);
  if (amount <= 0) {
    return { ok: true, sent: false, reason: "no balance owed" };
  }

  const token = buildPortalToken(deal);
  if (!token) {
    return { ok: true, sent: false, reason: "couldn't build portal token (missing name or street #)" };
  }

  const link = `https://portal.bardinroofing.com/status?token=${encodeURIComponent(token)}&tab=payment`;
  const firstName = (deal.homeowner_name || "there").split(/\s+/)[0];

  // Keep under 160 chars to avoid multi-segment billing on long messages.
  // Sample: "Bardin Roofing: Hi Michael, your first payment of $18,106.69 is ready. Pay here: https://portal.bardinroofing.com/status?token=...&tab=payment"
  const body = `Bardin Roofing: Hi ${firstName}, your ${slotLabel} of ${fmtUSD(amount)} is ready. Pay here: ${link}`;

  try {
    const { error } = await supabase.functions.invoke("twilio-send-sms", {
      body: {
        to: deal.homeowner_phone,
        body,
        deal_id: deal.id,
        tag: "payment_due_link",
      },
    });
    if (error) {
      console.warn("[notifyHomeownerPaymentDue] SMS send failed:", error);
      return { ok: false, sent: false, reason: error.message ?? "SMS send error" };
    }
    return { ok: true, sent: true };
  } catch (e) {
    const msg = fmtSupabaseError(e);
    console.warn("[notifyHomeownerPaymentDue] SMS send threw:", msg);
    return { ok: false, sent: false, reason: msg };
  }
}
